import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  useAcceptInvitationByToken,
  useInvitationByToken,
} from "model/useProgrammeInvitationQueries";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { initializeApp } from "services";
import { clearSessionState } from "services/auth";
import { useAppStore } from "stores";

const INVITE_TOKEN_STORAGE_KEY = "pendingInviteToken";

/**
 * Persists the token so it survives the login / signup round-trip. The
 * post-auth flow can read this and call accept automatically.
 */
function rememberToken(token: string) {
  window.localStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
}

/**
 * Seed the signup form with the details the inviter captured, so the farmer
 * doesn't have to retype them. Read back by SignupIntroDialog / SignupDialog.
 */
function stashSignupPrefills(data: {
  invitedEmail: string | null;
  invitedName: string | null;
  invitedTradingName: string | null;
}) {
  if (data.invitedEmail) {
    window.localStorage.setItem("signupEmail", data.invitedEmail);
  }
  if (data.invitedName) {
    window.localStorage.setItem("signupName", data.invitedName);
  }
  if (data.invitedTradingName) {
    window.localStorage.setItem("signupTradingName", data.invitedTradingName);
  }
}

export default function InviteLandingPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const user = useAppStore((s) => s.user);
  // True while initializeApp() is fetching /user/me after a fresh JWT was
  // stored (covers both login and signup post-auth handshake). Distinguishes
  // a freshly-issued JWT from a stale one left behind by a previous session.
  const isInitializingAuth = useAppStore((s) => s.loading === "pending");
  const { data, isLoading, error } = useInvitationByToken(token ?? null);
  const accept = useAcceptInvitationByToken();
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    if (token) rememberToken(token);
  }, [token]);

  useEffect(() => {
    // If a JWT exists but the user store isn't populated yet, the auth
    // handshake may not have run (e.g. cold load on this route, which isn't
    // wrapped in PrivateRoute). Kick it off so `user` populates and the main
    // effect below can take the auto-accept branch.
    if (window.localStorage.getItem("jwt") && !user && !isInitializingAuth) {
      void initializeApp();
    }
  }, [user, isInitializingAuth]);

  useEffect(() => {
    if (!token || !data) return;
    if (data.expired || data.inviteStatus === "REVOKED") return;

    const currentMail = user?.mail?.toLowerCase();
    const invitedMail = data.invitedEmail?.toLowerCase();

    // Stale / wrong session: a JWT is in the browser but it doesn't belong to
    // the invited email (e.g. a corporate user testing an invite to a new
    // farmer). Wipe auth state so the signup/login chain can start clean, then
    // re-seed the invite token + email so the post-auth bounce still works.
    if (user && invitedMail && currentMail !== invitedMail) {
      clearSessionState();
      window.localStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
      stashSignupPrefills(data);
      navigate(data.hasExistingAccount ? "/login" : "/signup-intro", {
        replace: true,
      });
      return;
    }

    // Signed in as the invited user (or no invited email to verify against):
    // auto-accept and land on the consent page, which mounts the programme
    // details dialog with the embedded consent form. The route doesn't
    // require a default farm so this works even for farmers who haven't set
    // up a farm yet.
    if (user) {
      void (async () => {
        try {
          const res = await accept.mutateAsync(token);
          window.localStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
          navigate(`/consent/${res.programmeGuid}`, { replace: true });
        } catch (e) {
          setAcceptError((e as Error).message);
        }
      })();
      return;
    }

    // Mid-auth handshake (post-signup or post-login) or a JWT in storage but
    // user not yet loaded (cold load): wait. Once `user` arrives the effect
    // re-runs and either the auto-accept or the mismatch branch above fires.
    if (isInitializingAuth) return;
    if (window.localStorage.getItem("jwt")) return;

    // Anonymous new-user invite: skip the intermediate landing card and go
    // straight to signup-intro with the email pre-seeded.
    if (!data.hasExistingAccount) {
      window.localStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
      stashSignupPrefills(data);
      navigate("/signup-intro", { replace: true });
    }
    // Anonymous existing-user invite: render the "Log in" CTA below.
  }, [
    user,
    isInitializingAuth,
    data?.programmeGuid,
    data?.hasExistingAccount,
    data?.invitedEmail,
    data?.expired,
    data?.inviteStatus,
    token,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 320,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ maxWidth: 560, mx: "auto", p: 3 }}>
        <Alert severity="error">
          This invitation link is invalid or has already been used. Please ask
          the programme team for a new invitation.
        </Alert>
      </Box>
    );
  }

  if (data.expired) {
    return (
      <Box sx={{ maxWidth: 560, mx: "auto", p: 3 }}>
        <Alert severity="warning">
          This invitation has expired. Please ask the programme team for a new
          one.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560, mx: "auto", p: 3 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          You're invited to {data.programmeName}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: "#52525b" }}>
          {data.invitedEmail
            ? `This invitation was sent to ${data.invitedEmail}.`
            : "Welcome to Ruumi."}
        </Typography>
        {acceptError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {acceptError}
          </Alert>
        )}
        {user ? (
          <Stack direction="row" spacing={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Accepting your invitation…</Typography>
          </Stack>
        ) : data.hasExistingAccount ? (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "#52525b" }}>
              You already have a Ruumi account. Log in to accept this
              invitation.
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                stashSignupPrefills(data);
                navigate("/login");
              }}
            >
              Log in
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "#52525b" }}>
              Create a Ruumi account to accept this invitation.
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                stashSignupPrefills(data);
                // `/signup-intro` is the proper entry point that also collects
                // name + phone, which we need for the corporate side to follow
                // up with the farmer.
                navigate("/signup-intro");
              }}
            >
              Sign up
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
